/**
 * useNavMenuItems — единый список пунктов навигации.
 *
 * Один и тот же список используют мобильное полноэкранное меню
 * (AppHeader.vue) и палитра быстрой навигации `/` (NavPalette.vue) — по
 * дизайн-спеке это буквально одни и те же пункты в двух местах. Состав
 * зависит от авторизации: авторизованному добавляются «Мои записи» /
 * «Профиль», админу — ещё и «Админ-панель».
 *
 * Вход в палитре оставлен намеренно: кнопку «Войти» из шапки убрали, и это
 * единственный способ попасть в админку, не набирая /admin-login руками.
 */
import { computed, type ComputedRef } from 'vue'
import { useAuthStore } from '@/stores/auth'

export interface NavMenuItem {
  /** Название пункта на русском. */
  label: string
  /** Латинский хинт (кикер) — показывается в палитре справа от названия. */
  hint: string
  /** Путь для vue-router. */
  to: string
}

export interface UseNavMenuItemsResult {
  items: ComputedRef<NavMenuItem[]>
}

export function useNavMenuItems(): UseNavMenuItemsResult {
  const auth = useAuthStore()

  const items = computed<NavMenuItem[]>(() => {
    const list: NavMenuItem[] = [
      { label: 'Главная', hint: 'home', to: '/' },
      { label: 'Проекты', hint: 'projects', to: '/projects' },
      { label: 'Медиа', hint: 'media', to: '/media' },
      { label: 'Дневник', hint: 'blog', to: '/blog' },
    ]

    if (auth.isAuthenticated) {
      list.push({ label: 'Мои записи', hint: 'private', to: '/diary' })
      list.push({ label: 'Профиль', hint: 'account', to: '/profile' })
      if (auth.isAdmin) {
        list.push({ label: 'Админ-панель', hint: 'admin', to: '/admin' })
      }
    } else {
      list.push({ label: 'Войти', hint: 'login', to: '/admin-login' })
    }

    return list
  })

  return { items }
}
